'use client';

import { useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import dynamic from 'next/dynamic';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useProducts } from '@/hooks/use-products';
import { useCategories } from '@/hooks/use-categories';
import { useCartStore } from '@/stores/cart-store';
import { useProductPopupStore } from '@/stores/product-popup-store';
import { api } from '@/lib/api';
import { bannersApi } from '@/lib/api/admin-banners';
import type { PublicPromotion } from '@/lib/api/content';
import type { ProductWithCategories } from '@/lib/api/products';
import { useStorefrontArt, type StorefrontMedia } from '@/lib/api/storefront-media';
import { isRouletteOffered, useRouletteConfig } from '@/hooks/use-roulette-config';
import {
  ymAddToCart,
  tmrAddToCart,
  ecomAdd,
  ymProductView,
  tmrProductView,
  ecomDetail,
} from '@/lib/analytics';
import { Footer } from '@/components/layout/footer';
import { PreorderBanner } from '@/components/features/preorder-banner';
import { UnpaidOrderBanner } from '@/components/features/orders';
import { Header } from '@/components/layout/header';
import { CampaignCovers } from './campaign-covers';
import { RollDogBanner } from './promotion-banners';
import { AppDownloadBanner } from './app-download-banner';
import { CatalogPromoCard, catalogPromos } from './catalog-promo-card';
import { CatalogCard } from './catalog-card';
import { RouletteCatalogCard } from './roulette-catalog-card';
import {
  catalogGroups,
  visibleCatalog,
  productPresentation,
  paidQuantities,
  isOrderable,
} from './catalog-model';
import { SetDayOffers } from './set-day-offers';
import { OrderAssistantEntry } from '@/components/features/order-assistant/order-assistant-entry';
import './storefront.css';
import './integration.css';

const BlogSection = dynamic(() =>
  import('@/components/features/blog/blog-section').then((m) => m.BlogSection)
);
const SeoBlock = dynamic(() =>
  import('@/components/features/hero/seo-block').then((m) => m.SeoBlock)
);

const subscribeMobile = (notify: () => void) => {
  const media = window.matchMedia('(max-width: 650px)');
  media.addEventListener('change', notify);
  return () => media.removeEventListener('change', notify);
};

export function StorefrontHome({ initialMedia }: { initialMedia?: StorefrontMedia }) {
  const mobile = useSyncExternalStore(
    subscribeMobile,
    () => window.matchMedia('(max-width: 650px)').matches,
    () => false
  );
  const [active, setActive] = useState('featured');
  const navRef = useRef<HTMLDivElement>(null);
  const productsQuery = useProducts();
  const categoriesQuery = useCategories();
  const art = useStorefrontArt(initialMedia);
  const promotionsQuery = useQuery({
    queryKey: ['storefront-promotions'],
    queryFn: async () =>
      (await api.get<PublicPromotion[]>('/content/promotions?platform=web')).data,
    staleTime: 60000,
  });
  const bannersQuery = useQuery({
    queryKey: ['banners', 'web'],
    queryFn: bannersApi.getActive,
    staleTime: 60000,
  });
  // The roulette box replaces the old checkout step: same query and the same "enabled" rule.
  const rouletteConfig = useRouletteConfig().data;
  const showRoulette = isRouletteOffered(rouletteConfig);
  const items = useCartStore((s) => s.items);
  const hydrated = useCartStore((s) => s._hasHydrated);
  const addItem = useCartStore((s) => s.addItem);
  const updateQuantity = useCartStore((s) => s.updateQuantity);
  const openPopup = useProductPopupStore((s) => s.openPopup);

  const products = useMemo(
    () => visibleCatalog(productsQuery.data || [], categoriesQuery.data || []),
    [productsQuery.data, categoriesQuery.data]
  );
  const groups = useMemo(
    () => catalogGroups(products, categoriesQuery.data || []),
    [products, categoriesQuery.data]
  );
  const quantities = useMemo(() => (hydrated ? paidQuantities(items) : {}), [hydrated, items]);
  const featured = useMemo(() => {
    const candidates = products.filter((p) => isOrderable(p) && (p.is_popular || p.is_new));
    const preferred = [100142, 100017, 22, 100154];
    return [...candidates]
      .sort(
        (a, b) =>
          (preferred.includes(a.id) ? preferred.indexOf(a.id) : 100) -
          (preferred.includes(b.id) ? preferred.indexOf(b.id) : 100)
      )
      .slice(0, 4);
  }, [products]);
  const sets = useMemo(
    () =>
      products
        .filter((p) => p.is_set_of_the_day)
        .sort((a, b) => a.id - b.id)
        .slice(0, 2),
    [products]
  );
  const setHostId = groups.find((group) => group.products.some((p) => p.id === sets[0]?.id))?.id;
  const rollDog = products.find((p) => p.id === 100053 && isOrderable(p));
  const hasFeatured = featured.length > 0 || (showRoulette && products.length > 0);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) setActive(visible[0].target.id);
      },
      { rootMargin: '-100px 0px -60% 0px' }
    );
    document
      .querySelectorAll('.storefront .product-section')
      .forEach((section) => observer.observe(section));
    return () => observer.disconnect();
  }, [groups, featured.length, showRoulette]);
  useEffect(() => {
    const nav = navRef.current;
    const button = nav?.querySelector<HTMLButtonElement>(`[data-section="${active}"]`);
    if (nav && button)
      nav.scrollTo({
        left: Math.max(
          0,
          button.offsetLeft - nav.offsetLeft - nav.clientWidth / 2 + button.clientWidth / 2
        ),
        behavior: 'auto',
      });
  }, [active]);

  const jump = (id: string) => {
    setActive(id);
    document.getElementById(id)?.scrollIntoView({
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches
        ? 'instant'
        : 'smooth',
      block: 'start',
    });
  };
  const showProduct = (p: ProductWithCategories) => {
    openPopup(p);
    ymProductView(p.id, p.name, p.price);
    tmrProductView();
    ecomDetail({ id: String(p.id), name: p.name, price: p.price });
  };
  const add = (p: ProductWithCategories) => {
    const current = paidQuantities(useCartStore.getState().items)[p.id] || 0;
    if (!isOrderable(p) || current >= 99) return;
    addItem(p);
    ymAddToCart(p.id, p.name, p.price, 1);
    tmrAddToCart();
    ecomAdd({ id: String(p.id), name: p.name, price: p.price, quantity: 1 });
    toast.success(`${p.name.trim()} — в корзине`, { duration: 1800 });
  };
  const card = (p: ProductWithCategories, wide = false, eager = false) => (
    <CatalogCard
      key={p.id}
      product={p}
      art={art[p.id]}
      quantity={quantities[p.id] || 0}
      wide={wide}
      eager={eager}
      onOpen={() => showProduct(p)}
      onAdd={() => add(p)}
      onQuantity={(q) => updateQuantity(p.id, Math.max(0, Math.min(99, q)))}
    />
  );

  return (
    <div className="storefront-page">
      <Header />
      <div className="storefront">
        <a className="skip-link" href="#menu">
          Перейти к меню
        </a>
        {promotionsQuery.isLoading || bannersQuery.isLoading ? (
          <div className="cover-loading" aria-label="Загружаем акции" />
        ) : (
          <CampaignCovers
            promotions={promotionsQuery.data || []}
            banners={bannersQuery.data || []}
            onChoose={() => jump('menu')}
          />
        )}
        {(promotionsQuery.isError || bannersQuery.isError) && (
          <div className="content-retry" role="status">
            Не все акции загрузились.{' '}
            <button
              onClick={() => {
                void promotionsQuery.refetch();
                void bannersQuery.refetch();
              }}
            >
              Повторить
            </button>
          </div>
        )}
      </div>
      <div className="storefront-notices">
        <PreorderBanner />
        <UnpaidOrderBanner />
      </div>
      <main className="storefront" id="catalog">
        <div className="menu-shell" id="menu">
          <div className="wrap menu-layout">
            <div className="catalog-main">
              <nav className="category-nav" aria-label="Категории меню">
                <div className="category-scroll" ref={navRef}>
                  {hasFeatured && (
                    <button
                      data-section="featured"
                      className={active === 'featured' ? 'active' : ''}
                      onClick={() => jump('featured')}
                    >
                      Для тебя
                    </button>
                  )}
                  {groups.map((c) => (
                    <button
                      key={c.id}
                      data-section={`category-${c.id}`}
                      className={active === `category-${c.id}` ? 'active' : ''}
                      onClick={() => jump(`category-${c.id}`)}
                    >
                      {c.name}
                    </button>
                  ))}
                </div>
              </nav>
              {productsQuery.isLoading || categoriesQuery.isLoading ? (
                <div className="catalog-loading" role="status">
                  Загружаем меню…
                </div>
              ) : productsQuery.isError || categoriesQuery.isError ? (
                <div className="catalog-loading" role="alert">
                  <h1>Не удалось загрузить меню</h1>
                  <p>Проверьте соединение и попробуйте ещё раз.</p>
                  <button
                    className="primary-button"
                    onClick={() => {
                      void productsQuery.refetch();
                      void categoriesQuery.refetch();
                    }}
                  >
                    Повторить
                  </button>
                </div>
              ) : (
                <>
                  {!!products.length && <OrderAssistantEntry />}
                  {hasFeatured && (
                    <section className="product-section featured-section" id="featured">
                      <div className="section-heading">
                        <h1>Попробуй что-то новое</h1>
                      </div>
                      <div className="product-grid">
                        {featured.slice(0, showRoulette ? 3 : 4).map((p) => card(p, false, true))}
                        {showRoulette && rouletteConfig && (
                          <RouletteCatalogCard config={rouletteConfig} />
                        )}
                      </div>
                    </section>
                  )}
                  {!hasFeatured && <h1 className="catalog-title">Наше меню</h1>}
                  {groups.map((c) => {
                    const regular = c.products.filter((p) => !sets.some((s) => s.id === p.id));
                    const wide = (p: ProductWithCategories) => {
                      const view = productPresentation(p, art[p.id]);
                      return view.wide && !!p.is_popular && !!view.scene;
                    };
                    const ordered = mobile
                      ? [...regular].sort((a, b) => Number(wide(b)) - Number(wide(a)))
                      : regular;
                    const cells = ordered.reduce((n, p) => n + (wide(p) ? 2 : 1), 0);
                    const promo = mobile && cells % 2 === 1 ? catalogPromos[c.id] : undefined;
                    const promoProduct = promo
                      ? products.find((p) => p.id === promo.productId && isOrderable(p))
                      : undefined;
                    return (
                      <section className="product-section" key={c.id} id={`category-${c.id}`}>
                        <div className="section-heading">
                          <h2>{c.name}</h2>
                        </div>
                        <div className="product-grid">
                          {ordered.map((p) => card(p, wide(p)))}
                          {promo && promoProduct && (
                            <CatalogPromoCard
                              image={promo.image}
                              title={promo.title}
                              lines={promo.lines}
                              productName={promoProduct.name}
                              onSelect={() => showProduct(promoProduct)}
                            />
                          )}
                        </div>
                        {setHostId === c.id && (
                          <SetDayOffers products={sets} onOpen={showProduct} />
                        )}
                      </section>
                    );
                  })}
                  {!groups.length && (
                    <p className="catalog-loading">
                      Меню скоро появится. Попробуйте зайти немного позже.
                    </p>
                  )}
                </>
              )}
            </div>
            <aside className="sidebar">
              <div className="sidebar-sticky">
                {rollDog && <RollDogBanner onTry={() => showProduct(rollDog)} />}
                <AppDownloadBanner />
              </div>
            </aside>
          </div>
          <div className="wrap storefront-mobile-app">
            <AppDownloadBanner />
          </div>
        </div>
      </main>
      <BlogSection />
      <div className="storefront-notices storefront-seo">
        <SeoBlock />
      </div>
      <Footer />
    </div>
  );
}
